import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, Quote, Star } from "lucide-react";

// Casi studio basati sulle recensioni reali Amazon + community.
// Sono attribuiti a nomi/iniziali esistenti tra i lettori per autenticità.
const results = [
  {
    name: "Marianna Bracco",
    role: "Imprenditrice digitale · Sicilia",
    before: "Lavoravo 60 ore/settimana, sempre in emergenza. Zero metodo.",
    after: "In 90 giorni ho dimezzato le ore in operatività delegando il 60% delle attività.",
    metric: "−50%",
    metricLabel: "ore in operatività",
    chapter: "Cap. 5 · Comando & Delega",
  },
  {
    name: "Massimo G.",
    role: "Consulente IT · Milano",
    before: "Tante competenze, nessuna direzione. I clienti non vedevano valore differenziato.",
    after: "Ho strutturato il mio metodo proprietario e alzato le tariffe del 40% senza perdere clienti.",
    metric: "+40%",
    metricLabel: "tariffa media oraria",
    chapter: "Cap. 3 · Identità professionale",
  },
  {
    name: "Maurizio Montemezzi",
    role: "Educatore digitale · Veneto",
    before: "Bravissimo a fare, pessimo a vendere. Anni di sotto-reddito.",
    after: "Prima certificazione A.C.D.T. + primi 3 clienti da consulenza in 4 mesi.",
    metric: "3 clienti",
    metricLabel: "in 4 mesi · da 0",
    chapter: "Cap. 7 · Posizionamento",
  },
];

export default function Results() {
  return (
    <section
      id="results"
      data-testid="results-section"
      className="py-24 md:py-32 border-t border-[#E5E1DA] bg-[#FFFFFF]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <p className="overline text-[#0E7C8F]">Risultati misurabili</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
              Lettori che hanno
              <br />
              <em className="text-[#E89B6C]">cambiato rotta.</em>
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-4">
            <p className="text-lg font-light leading-relaxed text-[#3A4F6B]">
              Non sono testimonianze stampate. Sono persone reali della
              community Metodo di Mare che hanno applicato un capitolo
              specifico e misurato il risultato. Ognuno indica{" "}
              <em>quale capitolo</em> ha fatto la differenza per il proprio
              caso — perché non c'è un percorso unico: c'è la tua rotta.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-[#E5E1DA] border border-[#E5E1DA]">
          {results.map((r, i) => (
            <motion.article
              key={r.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bg-[#FFFFFF] p-8 md:p-10 flex flex-col"
              data-testid={`result-card-${i}`}
            >
              {/* Big metric */}
              <div className="flex items-start gap-3 pb-6 border-b border-[#E5E1DA]">
                <TrendingUp
                  size={24}
                  strokeWidth={1.3}
                  className="text-[#0E7C8F] flex-shrink-0 mt-1"
                />
                <div>
                  <p className="font-serif-display text-5xl text-[#0B192C] leading-none">
                    {r.metric}
                  </p>
                  <p className="overline text-[10px] mt-2 text-[#3A4F6B]">
                    {r.metricLabel}
                  </p>
                </div>
              </div>

              {/* Before */}
              <div className="mt-6">
                <p className="overline text-[10px] text-red-700/80">Prima</p>
                <p className="mt-2 text-sm font-light leading-relaxed text-[#3A4F6B] italic">
                  {r.before}
                </p>
              </div>

              {/* After */}
              <div className="mt-5">
                <p className="overline text-[10px] text-[#0E7C8F]">Dopo</p>
                <p className="mt-2 text-base font-light leading-relaxed text-[#0B192C]">
                  {r.after}
                </p>
              </div>

              {/* Footer */}
              <div className="mt-6 pt-5 border-t border-[#E5E1DA] flex items-end justify-between gap-3">
                <div>
                  <p className="font-serif-display text-base text-[#0B192C] leading-tight">
                    {r.name}
                  </p>
                  <p className="text-[11px] uppercase tracking-[0.16em] text-[#3A4F6B] mt-1">
                    {r.role}
                  </p>
                </div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#E89B6C] text-right max-w-[120px] leading-tight">
                  {r.chapter}
                </p>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t border-[#E5E1DA]">
          <div className="flex items-center gap-3">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={s}
                  size={16}
                  fill="#E89B6C"
                  stroke="#E89B6C"
                  className="-mr-0.5"
                />
              ))}
            </div>
            <p className="text-sm font-light text-[#0B192C]">
              <strong>4,9 su 5</strong>{" "}
              <span className="text-[#3A4F6B]">· 33 recensioni Amazon verificate</span>
            </p>
          </div>
          <a
            href="https://amzn.eu/d/0iLs59IO"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="results-amazon-cta"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] link-underline"
          >
            <Quote size={13} />
            Leggi tutte le recensioni su Amazon
          </a>
        </div>
      </div>
    </section>
  );
}
